import React, { useState } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { MessageCircle, Mail, Phone, Send, BookOpen, Video, FileQuestion } from 'lucide-react';

export const HelpSupport = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        category: 'general',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Form submitted:', formData);
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setFormData({
                name: '',
                email: '',
                subject: '',
                category: 'general',
                message: '',
            });
        }, 3000);
    };

    return (
        <DashboardLayout title="Help & Support">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Contact Methods */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                        <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Mail className="h-6 w-6 text-blue-600" />
                        </div>
                        <h3 className="font-bold text-slate-900 mb-2">Email Support</h3>
                        <p className="text-sm text-slate-500 mb-3">support@contractai.com</p>
                        <p className="text-xs text-slate-400">Response within 24 hours</p>
                    </Card>

                    <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                        <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Phone className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="font-bold text-slate-900 mb-2">Phone Support</h3>
                        <p className="text-sm text-slate-500 mb-3">+1 (555) 123-4567</p>
                        <p className="text-xs text-slate-400">Mon-Fri, 9AM-5PM EST</p>
                    </Card>

                    <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                        <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <MessageCircle className="h-6 w-6 text-purple-600" />
                        </div>
                        <h3 className="font-bold text-slate-900 mb-2">Live Chat</h3>
                        <p className="text-sm text-slate-500 mb-3">Chat with our team</p>
                        <p className="text-xs text-slate-400">Available 24/7</p>
                    </Card>
                </div>

                {/* Contact Form */}
                <Card className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                            <Send className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">Send us a Message</h2>
                            <p className="text-sm text-slate-500">Fill out the form below and we'll get back to you soon</p>
                        </div>
                    </div>

                    {submitted && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                            <div className="h-8 w-8 bg-green-500 rounded-full flex items-center justify-center">
                                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <p className="font-medium text-green-900">Message sent successfully!</p>
                                <p className="text-sm text-green-700">We'll respond to your inquiry within 24 hours.</p>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Full Name *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Roger Smith"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Email Address *
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="roger.smith@example.com"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Category *
                                </label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="general">General Inquiry</option>
                                    <option value="technical">Technical Support</option>
                                    <option value="billing">Billing & Payments</option>
                                    <option value="feature">Feature Request</option>
                                    <option value="bug">Bug Report</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Subject *
                                </label>
                                <input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Brief description of your inquiry"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Message *
                            </label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                required
                                rows={6}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                                placeholder="Please describe your issue or question in detail..."
                            />
                        </div>

                        <div className="pt-2">
                            <Button type="submit" className="w-full md:w-auto">
                                <Send className="h-4 w-4 mr-2" />
                                Send Message
                            </Button>
                        </div>
                    </form>
                </Card>

                {/* FAQ Section */}
                <Card className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                            <FileQuestion className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">Frequently Asked Questions</h2>
                            <p className="text-sm text-slate-500">Quick answers to common questions</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <details className="group">
                            <summary className="flex items-center justify-between cursor-pointer list-none p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                                <span className="font-medium text-slate-900">How do I upload a document for analysis?</span>
                                <span className="transition group-open:rotate-180">
                                    <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24">
                                        <path d="M6 9l6 6 6-6"></path>
                                    </svg>
                                </span>
                            </summary>
                            <p className="text-slate-600 mt-3 px-4 pb-4">
                                Navigate to the Investment or Bonds dashboard, click the upload area or drag and drop your PDF document. The system will automatically process and analyze it.
                            </p>
                        </details>

                        <details className="group">
                            <summary className="flex items-center justify-between cursor-pointer list-none p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                                <span className="font-medium text-slate-900">What file formats are supported?</span>
                                <span className="transition group-open:rotate-180">
                                    <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24">
                                        <path d="M6 9l6 6 6-6"></path>
                                    </svg>
                                </span>
                            </summary>
                            <p className="text-slate-600 mt-3 px-4 pb-4">
                                Currently, we support PDF format for investment and bond contract analysis. Maximum file size is 10MB.
                            </p>
                        </details>

                        <details className="group">
                            <summary className="flex items-center justify-between cursor-pointer list-none p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                                <span className="font-medium text-slate-900">How long does analysis take?</span>
                                <span className="transition group-open:rotate-180">
                                    <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24">
                                        <path d="M6 9l6 6 6-6"></path>
                                    </svg>
                                </span>
                            </summary>
                            <p className="text-slate-600 mt-3 px-4 pb-4">
                                Analysis typically takes 30-60 seconds depending on document complexity and length. You'll see real-time progress during processing.
                            </p>
                        </details>

                        <details className="group">
                            <summary className="flex items-center justify-between cursor-pointer list-none p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                                <span className="font-medium text-slate-900">Can I export analysis results?</span>
                                <span className="transition group-open:rotate-180">
                                    <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24">
                                        <path d="M6 9l6 6 6-6"></path>
                                    </svg>
                                </span>
                            </summary>
                            <p className="text-slate-600 mt-3 px-4 pb-4">
                                Yes! You can export your analysis results as a PDF report using the "Download Report" button on the dashboard.
                            </p>
                        </details>
                    </div>
                </Card>

                {/* Resources */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                                <BookOpen className="h-5 w-5 text-indigo-600" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900">Documentation</h3>
                        </div>
                        <p className="text-slate-600 mb-4">
                            Explore our comprehensive documentation to learn about all features and capabilities.
                        </p>
                        <Button variant="outline">
                            View Documentation
                        </Button>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-10 w-10 bg-pink-100 rounded-lg flex items-center justify-center">
                                <Video className="h-5 w-5 text-pink-600" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900">Video Tutorials</h3>
                        </div>
                        <p className="text-slate-600 mb-4">
                            Watch step-by-step video guides to get the most out of ContractAI.
                        </p>
                        <Button variant="outline">
                            Watch Tutorials
                        </Button>
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
};
